/**
 * URL Validation Utility
 * 
 * Provides secure URL validation functions to prevent DOM-based XSS attacks
 * through javascript:, data:, vbscript:, and file: protocol injection.
 * 
 * This module is critical for security when handling URLs from untrusted sources
 * such as localStorage, sessionStorage, or user input.
 */

/**
 * List of dangerous URL protocols that can execute scripts
 */
const DANGEROUS_PROTOCOLS = [
  'javascript:', // eslint-disable-line no-script-url
  'data:',
  'vbscript:',
  'file:'
];

/**
 * List of safe URL protocols allowed for navigation
 */
const SAFE_PROTOCOLS = [
  'http:',
  'https:'
];

/**
 * Check if a URL protocol is safe for navigation
 * 
 * @param {string} protocol - The protocol to validate (e.g., 'http:', 'https:', 'javascript:')
 * @returns {boolean} True if the protocol is safe (http or https), false otherwise
 * 
 * @example
 * isSafeProtocol('http:')      // returns true
 * isSafeProtocol('https:')     // returns true
 * isSafeProtocol('javascript:') // returns false
 * isSafeProtocol('data:')      // returns false
 * 
 * @private - Exported for testing purposes only
 */
export const isSafeProtocol = (protocol) => {
  if (!protocol || typeof protocol !== 'string') {
    return false;
  }
  
  const normalizedProtocol = protocol.toLowerCase().trim();
  return SAFE_PROTOCOLS.includes(normalizedProtocol);
};

/**
 * Check if a URL has a valid and safe protocol
 * 
 * Validates that the URL uses http: or https: protocol only.
 * Rejects dangerous protocols like javascript:, data:, vbscript:, and file:
 * 
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL has a safe protocol, false otherwise
 * 
 * @example
 * hasValidProtocol('https://example.com')           // returns true
 * hasValidProtocol('http://example.com')            // returns true
 * hasValidProtocol('javascript:alert(1)')           // returns false
 * hasValidProtocol('data:text/html,<script>')       // returns false
 * hasValidProtocol('/relative/path')                // returns true (relative URLs are safe)
 */
export const hasValidProtocol = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();
  
  // Empty string is not valid
  if (trimmedUrl === '') {
    return false;
  }

  // Check for dangerous protocols first (case-insensitive)
  const lowerUrl = trimmedUrl.toLowerCase();
  for (const dangerousProtocol of DANGEROUS_PROTOCOLS) {
    if (lowerUrl.startsWith(dangerousProtocol)) {
      return false;
    }
  }

  // Relative URLs are safe (no protocol)
  if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
    return true;
  }

  // Check if URL contains a protocol separator
  // If it does, it should be a valid absolute URL
  if (trimmedUrl.includes('://') || trimmedUrl.includes(':')) {
    try {
      const urlObj = new URL(trimmedUrl);
      return isSafeProtocol(urlObj.protocol);
    } catch (error) {
      // If URL constructor fails with a protocol separator, it's malformed
      return false;
    }
  }

  // For URLs without protocol separator, try to parse as absolute URL
  // This handles cases like "example.com" which might be valid
  try {
    new URL(trimmedUrl, window.location.origin);
    // If it was parsed as a relative URL (same origin), it's safe
    return true;
  } catch (error) {
    // If URL constructor fails, it's malformed
    return false;
  }
};

/**
 * Validate that a URL is safe for navigation
 * 
 * Performs comprehensive validation to ensure the URL:
 * - Is not null, undefined, or empty
 * - Does not use dangerous protocols (javascript:, data:, vbscript:, file:)
 * - Uses only safe protocols (http:, https:) or is a relative URL
 * - Has valid URL structure
 * 
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is safe for navigation, false otherwise
 * 
 * @example
 * isSafeURL('https://example.com/page')     // returns true
 * isSafeURL('/relative/path')               // returns true
 * isSafeURL('javascript:alert(document.cookie)') // returns false
 * isSafeURL('data:text/html,<script>alert(1)</script>') // returns false
 * isSafeURL(null)                           // returns false
 * isSafeURL('')                             // returns false
 */
export const isSafeURL = (url) => {
  // Null, undefined, or non-string values are not safe
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();
  
  // Empty string is not safe
  if (trimmedUrl === '') {
    return false;
  }

  // Use hasValidProtocol for the actual validation
  return hasValidProtocol(trimmedUrl);
};

/**
 * Sanitize a URL for safe navigation with fallback
 * 
 * Validates the URL and returns it if safe, otherwise returns the fallback URL.
 * This function should be used before any window.location assignment or navigation.
 * 
 * Security considerations:
 * - Always validates URLs from untrusted sources (localStorage, user input, etc.)
 * - Logs warnings when unsafe URLs are detected
 * - Provides safe fallback to prevent navigation failures
 * 
 * @param {string} url - The URL to sanitize
 * @param {string} [fallback='/'] - The fallback URL to use if validation fails (defaults to '/')
 * @returns {string} The original URL if safe, otherwise the fallback URL
 * 
 * @example
 * sanitizeNavigationURL('https://example.com')  // returns 'https://example.com'
 * sanitizeNavigationURL('/dashboard')           // returns '/dashboard'
 * sanitizeNavigationURL('javascript:alert(1)')  // returns '/' (fallback)
 * sanitizeNavigationURL('javascript:alert(1)', '/home') // returns '/home'
 * sanitizeNavigationURL(null)                   // returns '/'
 * sanitizeNavigationURL('')                     // returns '/'
 */
export const sanitizeNavigationURL = (url, fallback = '/') => {
  // Validate the URL
  if (!isSafeURL(url)) {
    // Log security warning if a potentially malicious URL was detected
    if (url && typeof url === 'string' && url.trim() !== '') {
      console.warn('[Security] Unsafe URL detected and blocked:', url);
    }
    return fallback;
  }

  return url.trim();
};
